import React, { type CSSProperties } from "react";

import { VersionReviewDialog as BaseVersionReviewDialog } from "@/components/ui/VersionReviewDialog";
import { SaveStatusContent } from "@/components/ui/VersionReviewDialog/SaveStatusContent";

export type PhasedReviewDialogPhase = "review" | "saving" | "success";

interface PhasedReviewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  phase: PhasedReviewDialogPhase;
  reviewContent: React.ReactNode;
  statusTitle: string;
  statusDescription: string;
  reviewContentStyle?: CSSProperties;
  statusContentStyle?: CSSProperties;
  contentClassName?: string;
}

const DEFAULT_REVIEW_CONTENT_STYLE: CSSProperties = {
  width: "1100px",
  maxWidth: "80vw",
};

const DEFAULT_STATUS_CONTENT_STYLE: CSSProperties = {
  width: "420px",
  maxWidth: "92vw",
};

export const PhasedReviewDialog = ({
  isOpen,
  onClose,
  title,
  phase,
  reviewContent,
  statusTitle,
  statusDescription,
  reviewContentStyle = DEFAULT_REVIEW_CONTENT_STYLE,
  statusContentStyle = DEFAULT_STATUS_CONTENT_STYLE,
  contentClassName = "max-h-[90vh] overflow-hidden",
}: PhasedReviewDialogProps) => {
  const isStatusPhase = phase !== "review";

  return (
    <BaseVersionReviewDialog
      isOpen={isOpen}
      onClose={isStatusPhase ? () => undefined : onClose}
      title={title}
      contentClassName={contentClassName}
      contentStyle={isStatusPhase ? statusContentStyle : reviewContentStyle}
      shouldShowCloseButton={!isStatusPhase}
    >
      <div className="overflow-hidden">
        <div
          className={`grid transition-[grid-template-rows,opacity,transform] duration-300 ease-in-out ${
            isStatusPhase
              ? "pointer-events-none -translate-y-2 grid-rows-[0fr] opacity-0"
              : "translate-y-0 grid-rows-[1fr] opacity-100"
          }`}
        >
          <div className="min-h-0 overflow-hidden">{reviewContent}</div>
        </div>
        <div
          className={`grid transition-[grid-template-rows,opacity,transform] duration-300 ease-in-out ${
            isStatusPhase
              ? "translate-y-0 grid-rows-[1fr] opacity-100"
              : "pointer-events-none translate-y-2 grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="min-h-0 overflow-hidden">
            <div className="flex min-h-[220px] items-center justify-center">
              <SaveStatusContent
                state={phase === "review" ? "saving" : phase}
                title={statusTitle}
                description={statusDescription}
              />
            </div>
          </div>
        </div>
      </div>
    </BaseVersionReviewDialog>
  );
};
